import { isResource, resolveId } from '@medplum/core'
import type {
  Encounter,
  Patient,
  Procedure,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeableConcept,
  codeFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  MEAL_SERVED_IDENTIFIER_SYSTEM,
  MEAL_TYPE_SYSTEM,
} from '#src/codeSystems.ts'
import {
  endOfDateInLocalTimezone,
  startOfDateInLocalTimezone,
  today,
} from '#src/dates.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

export const MEAL_TYPE_CODES = ['breakfast', 'lunch', 'dinner'] as const
export type MealType = (typeof MEAL_TYPE_CODES)[number]

export const MEAL_TYPES: Record<
  MealType,
  { system: typeof MEAL_TYPE_SYSTEM; code: MealType; display: string }
> = {
  breakfast: {
    system: MEAL_TYPE_SYSTEM,
    code: 'breakfast',
    display: 'Breakfast',
  },
  lunch: {
    system: MEAL_TYPE_SYSTEM,
    code: 'lunch',
    display: 'Lunch',
  },
  dinner: {
    system: MEAL_TYPE_SYSTEM,
    code: 'dinner',
    display: 'Dinner',
  },
}

// A shared, device-independent record that a meal/diet ticket has been printed
// ("served") for a participant. Stored as a completed `Procedure` so every
// iPad running the Meal Ticket Printer reads the same served state instead of a
// per-device `localStorage` copy.
//
// One Procedure per (participant, local day, meal type): additive and
// concurrency-safe across devices. Identifier value is
// `{Patient/{id}}|{YYYY-MM-DD}|{mealType}`.
export class MealTicketServing extends ExtendedResource<Procedure> {
  static readonly RESOURCE_TYPE = 'Procedure'

  static isInstance(value: unknown): value is Procedure {
    return (
      isResource(value) &&
      isOfType(value, 'Procedure') &&
      (value.identifier ?? []).some(
        (identifier) => identifier.system === MEAL_SERVED_IDENTIFIER_SYSTEM
      )
    )
  }

  constructor({
    subject,
    mealType,
    encounter,
    performedDateTime,
  }: {
    subject: Reference<Patient>
    mealType: MealType
    encounter?: Reference<Encounter>
    performedDateTime?: string
  }) {
    invariant(
      subject.reference,
      'MealTicketServing requires a patient reference'
    )
    const date = today()
    const meal = MEAL_TYPES[mealType]
    super({
      status: 'completed',
      identifier: [
        {
          system: MEAL_SERVED_IDENTIFIER_SYSTEM,
          value: MealTicketServing.identifierValue(subject, date, mealType),
        },
      ],
      code: codeableConcept(meal),
      subject,
      encounter,
      performedDateTime: performedDateTime ?? now(),
    })
  }

  // Before 11:00 local is breakfast; 11:00 and after is lunch. Dinner stays
  // on the model for a later UI but is never chosen from the clock.
  static currentMealType(at: Date = new Date()): MealType {
    return at.getHours() < 11 ? 'breakfast' : 'lunch'
  }

  // Milliseconds until the next breakfast/lunch boundary (11:00 local, or
  // midnight when lunch rolls back to breakfast). Used so an idle kiosk
  // retargets served status when the clock crosses the cutover.
  static msUntilNextMealChange(at: Date = new Date()): number {
    const next = new Date(at)
    if (at.getHours() < 11) {
      next.setHours(11, 0, 0, 0)
    } else {
      next.setDate(next.getDate() + 1)
      next.setHours(0, 0, 0, 0)
    }
    return Math.max(next.getTime() - at.getTime(), 0)
  }

  static identifierValue(
    patient: Reference<Patient>,
    date: string,
    mealType: MealType
  ): string {
    invariant(
      patient.reference,
      'MealTicketServing identifier requires a patient reference'
    )
    return `${patient.reference}|${date}|${mealType}`
  }

  // Every meal-served Procedure for the local calendar day, any meal type.
  static servedTodayQuery(): QueryTypes {
    const date = today()
    return {
      ...MealTicketServing.byIdentifier(MEAL_SERVED_IDENTIFIER_SYSTEM),
      _filter: `date ge ${startOfDateInLocalTimezone(date)} and date le ${endOfDateInLocalTimezone(date)}`,
    }
  }

  // Create-if-none-exists uniqueness key: one Procedure per patient/day/meal.
  static servedTodayForPatientQuery(
    patient: Reference<Patient>,
    mealType: MealType
  ): QueryTypes {
    return MealTicketServing.byIdentifier(
      MEAL_SERVED_IDENTIFIER_SYSTEM,
      MealTicketServing.identifierValue(patient, today(), mealType)
    )
  }

  get patientId(): string | undefined {
    return resolveId(this.resource.subject)
  }

  get mealType(): MealType | undefined {
    const code = codeFromCodeableConcept(this.resource.code, MEAL_TYPE_SYSTEM)
    return MEAL_TYPE_CODES.find((mealType) => mealType === code)
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource.encounter
  }

  get performedDateTime(): string | undefined {
    return this.resource.performedDateTime
  }
}
